import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  StarIcon as StarIconSolid,
  CheckCircleIcon,
  XCircleIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  UserIcon,
  ShoppingBagIcon,
  ClockIcon
} from '@heroicons/react/24/solid';
import { StarIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

const AdminReviews = () => {
  const { user, loading: authLoading } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedReviews, setSelectedReviews] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  
  // Filters and search
  const [filters, setFilters] = useState({
    search: '',
    status: '', // 'pending', 'approved', 'rejected'
    rating: '', // '1', '2', '3', '4', '5'
    verified: '', // 'true', 'false'
    sortBy: 'created_at',
    sortOrder: 'desc'
  });

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });

  // Stats
  const [stats, setStats] = useState({
    totalReviews: 0,
    pendingReviews: 0,
    approvedReviews: 0,
    rejectedReviews: 0,
    averageRating: 0,
    verifiedReviews: 0
  });

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    
    if (authLoading) return; // Wait for auth to complete
    
    if (!user) {
      toast.error('אנא התחבר כדי לצפות בדף זה');
      return;
    }
    
    if (user.role !== 'admin') {
      toast.error('אין לך הרשאה לצפות בדף זה - נדרשות הרשאות מנהל');
      return;
    }

    // Load filters from URL
    const urlFilters = {
      search: searchParams.get('search') || '',
      status: searchParams.get('status') || '',
      rating: searchParams.get('rating') || '',
      verified: searchParams.get('verified') || '',
      sortBy: searchParams.get('sort') || 'created_at',
      sortOrder: searchParams.get('order') || 'desc'
    };
    setFilters(urlFilters);
    
    fetchReviews();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user, authLoading]);

  const fetchReviews = async (customFilters = null, page = null) => {
    try {
      setLoading(true);
      const currentFilters = customFilters || filters;
      const currentPage = page || pagination.currentPage;
      
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pagination.itemsPerPage.toString(),
        ...currentFilters
      });

      const response = await axios.get(`/api/admin/reviews?${params}`);
      
      setReviews(response.data.reviews);
      setPagination({
        ...pagination,
        currentPage: response.data.pagination.currentPage,
        totalPages: response.data.pagination.totalPages,
        totalItems: response.data.pagination.totalItems
      });
    } catch (error) {
      toast.error(`שגיאה בטעינת הביקורות: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/admin/reviews/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching review stats:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    // Update URL
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    setSearchParams(params);
    
    // Reset to first page and fetch
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    fetchReviews(newFilters, 1);
  };

  const handleStatusChange = async (reviewId, newStatus) => {
    try {
      await axios.put(`/api/admin/reviews/${reviewId}/status`, { status: newStatus });
      
      // Update the review in the list
      setReviews(prev => prev.map(review => 
        review.id === reviewId ? { ...review, status: newStatus } : review
      ));
      
      toast.success(`הביקורת ${newStatus === 'approved' ? 'אושרה' : 'נדחתה'} בהצלחה`);
      fetchStats(); // Refresh stats
    } catch (error) {
      toast.error('שגיאה בעדכון סטטוס הביקורת');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק את הביקורת?')) {
      return;
    }

    try {
      await axios.delete(`/api/admin/reviews/${reviewId}`);
      
      // Remove the review from the list
      setReviews(prev => prev.filter(review => review.id !== reviewId));
      
      toast.success('הביקורת נמחקה בהצלחה');
      fetchStats(); // Refresh stats
    } catch (error) {
      toast.error('שגיאה במחיקת הביקורת');
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedReviews.length === 0) {
      toast.error('אנא בחר ביקורות לביצוע הפעולה');
      return;
    }

    if (!window.confirm(`האם אתה בטוח שברצונך ${action === 'approve' ? 'לאשר' : action === 'reject' ? 'לדחות' : 'למחוק'} ${selectedReviews.length} ביקורות?`)) {
      return;
    }

    try {
      await axios.post('/api/admin/reviews/bulk-action', {
        reviewIds: selectedReviews,
        action
      });

      if (action === 'delete') {
        setReviews(prev => prev.filter(review => !selectedReviews.includes(review.id)));
      } else {
        setReviews(prev => prev.map(review => 
          selectedReviews.includes(review.id) ? { ...review, status: action === 'approve' ? 'approved' : 'rejected' } : review
        ));
      }

      setSelectedReviews([]);
      setShowBulkActions(false);
      toast.success(`${selectedReviews.length} ביקורות עודכנו בהצלחה`);
      fetchStats();
    } catch (error) {
      toast.error('שגיאה בביצוע הפעולה');
    }
  };

  const handleSelectReview = (reviewId) => {
    setSelectedReviews(prev => {
      const newSelected = prev.includes(reviewId) 
        ? prev.filter(id => id !== reviewId)
        : [...prev, reviewId];
      
      setShowBulkActions(newSelected.length > 0);
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    if (selectedReviews.length === reviews.length) {
      setSelectedReviews([]);
      setShowBulkActions(false);
    } else {
      setSelectedReviews(reviews.map(review => review.id));
      setShowBulkActions(true);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          star <= rating ? (
            <StarIconSolid key={star} className="h-4 w-4 text-yellow-400" />
          ) : (
            <StarIcon key={star} className="h-4 w-4 text-gray-300" />
          )
        ))}
      </div>
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', text: 'ממתין לאישור' },
      approved: { color: 'bg-green-100 text-green-800', text: 'מאושר' },
      rejected: { color: 'bg-red-100 text-red-800', text: 'נדחה' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Show loading while auth is being checked
  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Check if user is admin
  if (!user || user.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">אין הרשאה</h2>
          <p className="text-gray-600">אין לך הרשאה לצפות בדף זה</p>
        </div>
      </div>
    );
  }

  if (loading && reviews.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">ניהול ביקורות</h1>
          <p className="text-gray-600">נהל ואשר ביקורות לקוחות</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <StarIconSolid className="h-8 w-8 text-blue-600" />
            </div>
            <div className="mr-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">סה"כ ביקורות</dt>
                <dd className="text-lg font-medium text-gray-900">{stats.totalReviews}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClockIcon className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="mr-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">ממתינות לאישור</dt>
                <dd className="text-lg font-medium text-gray-900">{stats.pendingReviews}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="mr-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">מאושרות</dt>
                <dd className="text-lg font-medium text-gray-900">{stats.approvedReviews}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <XCircleIcon className="h-8 w-8 text-red-600" />
            </div>
            <div className="mr-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">נדחות</dt>
                <dd className="text-lg font-medium text-gray-900">{stats.rejectedReviews}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ShoppingBagIcon className="h-8 w-8 text-purple-600" />
            </div>
            <div className="mr-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">קנייה מאומתת</dt>
                <dd className="text-lg font-medium text-gray-900">{stats.verifiedReviews}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <StarIconSolid className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="mr-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">דירוג ממוצע</dt>
                <dd className="text-lg font-medium text-gray-900">{stats.averageRating?.toFixed(1) || '0.0'}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">סינון וחיפוש</h3>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <FunnelIcon className="h-4 w-4 ml-2" />
              {showFilters ? 'הסתר מסננים' : 'הצג מסננים'}
            </button>
          </div>

          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="חיפוש ביקורות..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="block w-full pr-10 border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">סטטוס</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="block w-full border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">כל הסטטוסים</option>
                  <option value="pending">ממתין לאישור</option>
                  <option value="approved">מאושר</option>
                  <option value="rejected">נדחה</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">דירוג</label>
                <select
                  value={filters.rating}
                  onChange={(e) => handleFilterChange('rating', e.target.value)}
                  className="block w-full border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">כל הדירוגים</option>
                  <option value="5">5 כוכבים</option>
                  <option value="4">4 כוכבים</option>
                  <option value="3">3 כוכבים</option>
                  <option value="2">2 כוכבים</option>
                  <option value="1">1 כוכב</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">קנייה מאומתת</label>
                <select
                  value={filters.verified}
                  onChange={(e) => handleFilterChange('verified', e.target.value)}
                  className="block w-full border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">הכל</option>
                  <option value="true">קנייה מאומתת</option>
                  <option value="false">לא מאומת</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">מיון</label>
                <select
                  value={`${filters.sortBy}-${filters.sortOrder}`}
                  onChange={(e) => {
                    const [sortBy, sortOrder] = e.target.value.split('-');
                    handleFilterChange('sortBy', sortBy);
                    handleFilterChange('sortOrder', sortOrder);
                  }}
                  className="block w-full border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="created_at-desc">תאריך יצירה (חדש לישן)</option>
                  <option value="created_at-asc">תאריך יצירה (ישן לחדש)</option>
                  <option value="rating-desc">דירוג (גבוה לנמוך)</option>
                  <option value="rating-asc">דירוג (נמוך לגבוה)</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bulk Actions */}
      {showBulkActions && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-blue-700">
              נבחרו {selectedReviews.length} ביקורות
            </span>
            <div className="flex space-x-2">
              <button
                onClick={() => handleBulkAction('approve')}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
              >
                <CheckCircleIcon className="h-4 w-4 ml-1" />
                אשר הכל
              </button>
              <button
                onClick={() => handleBulkAction('reject')}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700"
              >
                <XCircleIcon className="h-4 w-4 ml-1" />
                דחה הכל
              </button>
              <button
                onClick={() => handleBulkAction('delete')}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-gray-600 hover:bg-gray-700"
              >
                <TrashIcon className="h-4 w-4 ml-1" />
                מחק הכל
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reviews Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              ביקורות ({pagination.totalItems})
            </h3>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={selectedReviews.length === reviews.length && reviews.length > 0}
                onChange={handleSelectAll}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label className="mr-2 text-sm text-gray-700">בחר הכל</label>
            </div>
          </div>
        </div>

        <ul className="divide-y divide-gray-200">
          {reviews.map((review) => (
            <li key={review.id} className="px-4 py-4 sm:px-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={selectedReviews.includes(review.id)}
                    onChange={() => handleSelectReview(review.id)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        <UserIcon className="h-5 w-5 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">
                          {review.user_name}
                        </span>
                      </div>
                      {review.is_verified_purchase && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          <ShoppingBagIcon className="h-3 w-3 ml-1" />
                          קנייה מאומתת
                        </span>
                      )}
                      {renderStars(review.rating)}
                      {getStatusBadge(review.status)}
                    </div>
                    <span className="text-sm text-gray-500">
                      {formatDate(review.created_at)}
                    </span>
                  </div>

                  <div className="mt-2">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <span className="font-medium">מוצר:</span>
                      <span>{review.product?.name}</span>
                    </div>
                  </div>

                  {review.title && (
                    <div className="mt-2">
                      <h4 className="text-sm font-medium text-gray-900">{review.title}</h4>
                    </div>
                  )}

                  {review.comment && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-700">{review.comment}</p>
                    </div>
                  )}

                  <div className="mt-4 flex items-center space-x-4">
                    {review.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleStatusChange(review.id, 'approved')}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircleIcon className="h-4 w-4 ml-1" />
                          אשר
                        </button>
                        <button
                          onClick={() => handleStatusChange(review.id, 'rejected')}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                        >
                          <XCircleIcon className="h-4 w-4 ml-1" />
                          דחה
                        </button>
                      </>
                    )}
                    
                    {review.status === 'approved' && (
                      <button
                        onClick={() => handleStatusChange(review.id, 'rejected')}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <XCircleIcon className="h-4 w-4 ml-1" />
                        דחה
                      </button>
                    )}
                    
                    {review.status === 'rejected' && (
                      <button
                        onClick={() => handleStatusChange(review.id, 'approved')}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <CheckCircleIcon className="h-4 w-4 ml-1" />
                        אשר
                      </button>
                    )}

                    <button
                      onClick={() => handleDeleteReview(review.id)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                    >
                      <TrashIcon className="h-4 w-4 ml-1" />
                      מחק
                    </button>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>

        {reviews.length === 0 && !loading && (
          <div className="text-center py-12">
            <StarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">אין ביקורות</h3>
            <p className="mt-1 text-sm text-gray-500">לא נמצאו ביקורות התואמות את הקריטריונים</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
              disabled={pagination.currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              הקודם
            </button>
            <button
              onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
              disabled={pagination.currentPage === pagination.totalPages}
              className="mr-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              הבא
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                מציג{' '}
                <span className="font-medium">
                  {(pagination.currentPage - 1) * pagination.itemsPerPage + 1}
                </span>{' '}
                עד{' '}
                <span className="font-medium">
                  {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
                </span>{' '}
                מתוך{' '}
                <span className="font-medium">{pagination.totalItems}</span> תוצאות
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                  disabled={pagination.currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  הקודם
                </button>
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPagination(prev => ({ ...prev, currentPage: pageNum }))}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        pageNum === pagination.currentPage
                          ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  הבא
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReviews;
